import { loadVimeoApi } from './vimeo-api.js';
import { buildPlayButtonIcon } from './play-button-icon.js';

/*
  Elementor's native Video widget, used with its own "Lightbox" click
  action. The video link is whatever is set in that widget's own "Video
  Link" field (Content tab) - Elementor reads it and builds the lightbox
  iframe itself; nothing here supplies or looks up a URL.

  This module only:

  1) Replaces Elementor's default play icon with a custom animated one, on
     every video widget - including ones Elementor inserts later via AJAX
     (Elementor fires `frontend/element_ready/video.default` for those).

  2) Once the lightbox opens, Vimeo embeds sometimes fail to autoplay/mute
     on their own inside the dialog. This forces autoplay+muted params onto
     the iframe src Elementor already inserted and nudges playback through
     the Vimeo Player API, then closes the lightbox when the video ends.
*/

var started = false;

function initPlayButtons(root) {
    root = root || document;

    root.querySelectorAll('.elementor-widget-video .elementor-custom-embed-play').forEach(function (play) {
        if (play.querySelector('.cv-btn')) {
            return;
        }

        play.innerHTML = '';
        play.appendChild(buildPlayButtonIcon('cv'));
    });
}

function isVimeoSrc(src) {
    return !!src && src.indexOf('player.vimeo.com') !== -1;
}

function addParams(url, params) {
    try {
        var u = new URL(url, window.location.href);
        Object.keys(params).forEach(function (key) {
            u.searchParams.set(key, params[key]);
        });
        return u.toString();
    } catch (e) {
        var glue = url.indexOf('?') > -1 ? '&' : '?';
        var query = [];
        for (var key in params) {
            query.push(encodeURIComponent(key) + '=' + encodeURIComponent(params[key]));
        }
        return url + glue + query.join('&');
    }
}

function getLightbox() {
    return document.querySelector('.elementor-lightbox');
}

/*
  Elementor's lightbox is a single reused dialog instance, so at most one
  Vimeo iframe is ever inside it at a time.
*/
function findVimeoIframe(root) {
    if (!root) {
        return null;
    }

    var iframes = root.querySelectorAll('iframe');

    for (var i = iframes.length - 1; i >= 0; i--) {
        var iframe = iframes[i];
        var src = iframe.getAttribute('src') || iframe.getAttribute('data-src') || '';

        if (isVimeoSrc(src)) {
            return iframe;
        }
    }

    return null;
}

/*
  The dialogs-manager library that builds this widget always tags its close
  control with this class, regardless of locale or icon markup.
*/
function closeLightbox(lightbox) {
    if (!lightbox) {
        return;
    }

    var closeButton = lightbox.querySelector('.dialog-close-button');

    if (closeButton && closeButton.click) {
        closeButton.click();
    }
}

function forceAutoplaySrc(iframe) {
    var src = iframe.getAttribute('src') || '';
    var dataSrc = iframe.getAttribute('data-src') || '';
    var base = src || dataSrc;

    if (!isVimeoSrc(base)) {
        return false;
    }

    var newSrc = addParams(base, {
        autoplay: 1,
        autopause: 0,
        muted: 1,
        playsinline: 1,
        dnt: 1
    });

    iframe.setAttribute('allow', 'autoplay; fullscreen; picture-in-picture');
    iframe.setAttribute('playsinline', '1');

    if (iframe.getAttribute('data-src')) {
        iframe.setAttribute('data-src', newSrc);
    }

    if (iframe.getAttribute('src') !== newSrc) {
        iframe.setAttribute('src', newSrc);
    } else {
        /*
          Same URL string: the browser will not reload an iframe just
          because its src was reassigned. Blank it first to force a reload.
        */
        iframe.setAttribute('src', '');
        setTimeout(function () {
            iframe.setAttribute('src', newSrc);
        }, 30);
    }

    return true;
}

function tryPlayWithPostMessage(iframe) {
    if (!iframe || !iframe.contentWindow) {
        return;
    }

    try {
        iframe.contentWindow.postMessage(JSON.stringify({ method: 'setVolume', value: 0 }), '*');
        iframe.contentWindow.postMessage(JSON.stringify({ method: 'play' }), '*');
    } catch (e) {
        /*
          Cross-origin postMessage failures are not actionable here.
        */
    }
}

function playVimeo(lightbox, iframe, players) {
    if (!iframe || !forceAutoplaySrc(iframe)) {
        return;
    }

    loadVimeoApi(function () {
        var played = false;

        var startPlayback = function () {
            if (played) {
                return;
            }

            tryPlayWithPostMessage(iframe);

            try {
                var player = players.get(iframe);

                if (!player) {
                    player = new Vimeo.Player(iframe);
                    players.set(iframe, player);

                    player.on('ended', function () {
                        closeLightbox(lightbox);
                    });
                }

                player.ready()
                    .then(function () {
                        return player.setMuted(true).catch(function () {});
                    })
                    .then(function () {
                        return player.play();
                    })
                    .then(function () {
                        played = true;
                    })
                    .catch(function () {
                        tryPlayWithPostMessage(iframe);
                    });
            } catch (e) {
                tryPlayWithPostMessage(iframe);
            }
        };

        if (iframe.dataset.cvLoaded === '1') {
            startPlayback();
        } else {
            var onLoad = function () {
                iframe.dataset.cvLoaded = '1';
                iframe.removeEventListener('load', onLoad);
                setTimeout(startPlayback, 120);
            };

            iframe.addEventListener('load', onLoad, { once: true });
            setTimeout(startPlayback, 400);
            setTimeout(startPlayback, 1200);
        }
    });
}

function waitForNewIframeAndPlay(token, getClickToken, players) {
    var startedAt = Date.now();
    var lastIframe = null;

    var timer = setInterval(function () {
        if (token !== getClickToken()) {
            clearInterval(timer);
            return;
        }

        var lightbox = getLightbox();
        var iframe = lightbox ? findVimeoIframe(lightbox) : null;

        if (iframe && (iframe !== lastIframe || !iframe.dataset.cvAutoplayHandled)) {
            lastIframe = iframe;
            iframe.dataset.cvAutoplayHandled = '1';
            playVimeo(lightbox, iframe, players);
        }

        if (Date.now() - startedAt > 7000) {
            clearInterval(timer);
        }
    }, 150);
}

/*
  Elementor may have already fired `elementor/frontend/init` before this
  script attached its listener (enqueue order between the two is not
  guaranteed) - check for an already-initialized frontend first, and only
  fall back to waiting for the event.
*/
function registerElementorHook() {
    if (window.elementorFrontend && elementorFrontend.hooks) {
        elementorFrontend.hooks.addAction('frontend/element_ready/video.default', function ($scope) {
            initPlayButtons($scope[0]);
        });
        return;
    }

    window.addEventListener('elementor/frontend/init', registerElementorHook, { once: true });
}

export function initElementorVideoLightbox() {
    initPlayButtons();

    if (started) {
        return;
    }

    started = true;
    registerElementorHook();

    var clickToken = 0;
    var players = new WeakMap();

    document.addEventListener('click', function (e) {
        var trigger = e.target.closest('.elementor-custom-embed-play, .elementor-custom-embed-image-overlay');

        if (!trigger) {
            return;
        }

        clickToken++;
        waitForNewIframeAndPlay(clickToken, function () { return clickToken; }, players);
    }, true);
}
